import React, { useState, useEffect } from "react";
import axios from "axios";
import CommonTable from "../CommonTable";
import { BASE_URL } from "../../api";
import { getApiHeaders, formatDateDDMMYYYY } from "../../utils/helpers";

export default function ParticipantHistory({ participants, onStatusLoaded }) {
  const [data, setData] = useState(participants || []);
  const [loading, setLoading] = useState(false);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}/account/learn_earn_status`, {
        headers: getApiHeaders(),
      });

      if (
        (res.data?.code === 200 || res.data?.status === "success") &&
        res.data?.data
      ) {
        const raw = res.data.data;
        const list = Array.isArray(raw) ? raw : [raw];
        setData(list);
        if (onStatusLoaded) {
          onStatusLoaded(Array.isArray(raw) ? raw[0] : raw);
        }
      } else {
        setData([]);
      }
    } catch (error) {
      console.error("Error fetching learn & earn status:", error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!participants || participants.length === 0) {
      fetchStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stickyHeaderStyle = {
    position: "sticky",
    top: 0,
    backgroundColor: "var(--bs-card-bg, #ffffff)",
    zIndex: 2,
    boxShadow: "0 1px 0 var(--bs-border-color, #e9ecef)",
  };

  const columns = [
    {
      header: "Level No",
      headerStyle: stickyHeaderStyle,
      render: (row) => (
        <span className="fw-semibold text-dark">
          {row.current_level_no ?? row.level_no ?? row.levelNo ?? "-"}
        </span>
      ),
    },
    {
      header: "Level Name",
      headerStyle: stickyHeaderStyle,
      render: (row) => (
        <span className="fw-medium">
          {row.current_level_name ?? row.level_name ?? row.levelName ?? "-"}
        </span>
      ),
    },
    {
      header: "Point",
      headerStyle: stickyHeaderStyle,
      render: (row) => {
        const pts = row.total_points ?? row.point ?? row.points ?? 0;
        return (
          <span className="fw-bold font-size-13 text-success">
            {pts}
          </span>
        );
      },
    },
    {
      header: "Joining Date",
      headerStyle: stickyHeaderStyle,
      render: (row) => (
        <span className="font-size-12 text-muted">
          {formatDateDDMMYYYY(row.joining_date || row.joiningDate) || "-"}
        </span>
      ),
    },
    {
      header: "Status",
      headerStyle: stickyHeaderStyle,
      render: (row) => {
        const status = row.status || "Active";
        const isActive = String(status).toLowerCase() === "active";
        return (
          <span
            className={`badge fw-bold px-3 py-1 rounded-pill ${isActive
              ? "bg-success bg-opacity-10 text-success"
              : "bg-secondary bg-opacity-10 text-secondary"
              }`}
          >
            {status}
          </span>
        );
      },
    },
  ];

  return (
    <div className="card shadow-sm border-0 mb-0">
      <div className="card-body">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h4 className="card-title mb-0">Participant History</h4>
        </div>
        <CommonTable
          columns={columns}
          data={data}
          loading={loading}
          disablePagination={true}
          containerStyle={{
            height: "360px",
            maxHeight: "360px",
            overflowY: "auto",
            scrollbarWidth: "thin",
          }}
          loadingMessage={
            <div>
              <div
                className="spinner-border text-primary spinner-border-sm me-2"
                role="status"
              ></div>
              <span>Loading participant status...</span>
            </div>
          }
          noDataMessage="No participant record found"
          tableClassName="table table-hover align-middle table-nowrap mb-0"
        />
      </div>
    </div>
  );
}
